import { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Label } from "../../@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../@/components/ui/select";

export default function RecipientsSelectorDialog({
  open,
  onOpenChange,
  cohortOptions = [],
  pathwayOptions = [],
  recipientType = "cohort",
  onRecipientTypeChange,
  selectedCohorts = [],
  setSelectedCohorts,
  selectedPathways = [],
  setSelectedPathways,
}) {
  const availableCohorts = useMemo(
    () => cohortOptions.filter((c) => !selectedCohorts.includes(String(c.value))),
    [cohortOptions, selectedCohorts]
  );

  const availablePathways = useMemo(
    () => pathwayOptions.filter((p) => !selectedPathways.includes(String(p.value))),
    [pathwayOptions, selectedPathways]
  );

  const hasAnyRecipients =
    (selectedCohorts?.length || 0) + (selectedPathways?.length || 0) > 0;

  const handleAddItem = (value) => {
    if (!value) return;

    if (recipientType === "cohort") {
      setSelectedCohorts?.((prev = []) =>
        prev.includes(value) ? prev : [...prev, value]
      );
      return;
    }

    setSelectedPathways?.((prev = []) =>
      prev.includes(value) ? prev : [...prev, value]
    );
  };

  const handleRemoveItem = (id) => {
    if (recipientType === "cohort") {
      setSelectedCohorts?.((prev = []) => prev.filter((value) => value !== id));
      return;
    }

    setSelectedPathways?.((prev = []) => prev.filter((value) => value !== id));
  };

  const selectedChips = useMemo(() => {
    if (recipientType === "cohort") {
      return selectedCohorts.map((id) => {
        const found = cohortOptions.find((c) => String(c.value) === String(id));
        return { id: String(id), label: found?.label || `Cohort ${id}` };
      });
    }

    return selectedPathways.map((id) => {
      const found = pathwayOptions.find((p) => String(p.value) === String(id));
      return { id: String(id), label: found?.label || `Pathway ${id}` };
    });
  }, [recipientType, selectedCohorts, selectedPathways, cohortOptions, pathwayOptions]);

  const secondSelectDisabled =
    recipientType === "cohort"
      ? cohortOptions.length === 0 || availableCohorts.length === 0
      : pathwayOptions.length === 0 || availablePathways.length === 0;

  const secondSelectPlaceholder =
    recipientType === "cohort"
      ? cohortOptions.length === 0
        ? "No cohorts available"
        : availableCohorts.length === 0
          ? "All cohorts selected"
          : "Select cohort(s)"
      : pathwayOptions.length === 0
        ? "No pathways available"
        : availablePathways.length === 0
          ? "All pathways selected"
          : "Select pathway(s)";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md h-auto">
        <DialogHeader>
          <DialogTitle>Select Recipients</DialogTitle>
          <p className="text-sm text-(--neutral-600) font-body">
            Choose which cohorts or pathways should receive the announcement.
          </p>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label
              htmlFor="recipientType"
              className="font-heading mb-2 text-(--neutral-900)"
            >
              Send To*
            </Label>
            <Select value={recipientType} onValueChange={onRecipientTypeChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select recipient type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cohort">Cohort(s)</SelectItem>
                <SelectItem value="pathway">Pathway(s)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label
              htmlFor="recipients"
              className="font-heading mb-2 text-(--neutral-900)"
            >
              {recipientType === "cohort" ? "Cohort(s)*" : "Pathway(s)*"}
            </Label>
            <Select value="" onValueChange={handleAddItem} disabled={secondSelectDisabled}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={secondSelectPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {recipientType === "cohort"
                  ? availableCohorts.map((opt) => (
                      <SelectItem key={opt.value} value={String(opt.value)}>
                        {opt.label}
                      </SelectItem>
                    ))
                  : availablePathways.map((opt) => (
                      <SelectItem key={opt.value} value={String(opt.value)}>
                        {opt.label}
                      </SelectItem>
                    ))}
              </SelectContent>
            </Select>

            {selectedChips.length > 0 ? (
              <div className="flex flex-wrap gap-2 mt-3">
                {selectedChips.map((chip) => (
                  <div key={chip.id} className="tag-chip">
                    <span>{chip.label}</span>
                    <button
                      type="button"
                      className="chip-remove"
                      onClick={() => handleRemoveItem(chip.id)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-(--color-destructive) mt-2">
                {hasAnyRecipients
                  ? `Add at least one ${recipientType} to continue.`
                  : "Select at least one cohort or pathway to continue."}
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => onOpenChange(false)} disabled={!hasAnyRecipients}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
